import { createContext, useContext, type ReactNode } from "react";

import type { Session } from "@/types/session";

/**
 * The authenticated session, populated by `<AppShell>` once `/api/session`
 * resolves. The shell holds rendering until this is non-null, so anything
 * inside the shell tree can rely on a guaranteed-present session via
 * `useAuthSession()` — no `session?.foo` branches.
 *
 * Only the shell calls `useSession()` directly. Every other consumer reads
 * through this context.
 */
const SessionContext = createContext<Session | null>(null);

interface SessionProviderProps {
  session: Session;
  children: ReactNode;
}

export function SessionProvider({ session, children }: SessionProviderProps) {
  return (
    <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
  );
}

/**
 * Reads the authenticated session. Throws if used outside `<SessionProvider>`
 * — that's a programming error (the shell should be in scope). Components
 * that need to render before a session exists (login page, etc.) live outside
 * the shell and don't call this.
 */
export function useAuthSession(): Session {
  const session = useOptionalAuthSession();
  if (!session) {
    throw new Error(
      "useAuthSession() must be used inside <AppShell>. Public/auth routes that " +
        "render outside the shell cannot read the session.",
    );
  }
  return session;
}

/**
 * The session if there is one, `null` otherwise — for code that must not take
 * a page down when it renders outside the shell.
 *
 * `useAuthSession` throwing is right for a component that genuinely cannot
 * function without a session. It is wrong for OPTIONAL chrome: a widget that
 * reads a feature flag to decide whether to render at all should decide "no"
 * outside the shell, not throw and take its host with it. Feature gates
 * (`useFeatures`) read through this for exactly that reason.
 */
export function useOptionalAuthSession(): Session | null {
  return useContext(SessionContext) ?? null;
}
